import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { Info, X } from 'lucide-react';
import type { SheetDetail as Detail } from './i18n';
import { useT } from './lang';

// Building blocks for V2.5 Klick-Flow kurz. Copied from flow-serious/ui.tsx
// (variants stay independent): calm sheet (tween, no spring), FitBox
// no-scroll guarantee, info buttons that open the sheet.

export const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Last-resort "never scroll, never cut off" guarantee. The screen is laid out
 * for 375×667 already; if a screen still ends up taller than the available
 * content area (tiny viewport, big system font), it is scaled down uniformly
 * instead of overflowing. Content is vertically centred when it fits.
 */
export function FitBox({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const o = outer.current;
    const i = inner.current;
    if (!o || !i) return;
    const measure = () => {
      const avail = o.clientHeight;
      const need = i.offsetHeight; // layout height – ignores our own transform
      const next = need > avail && avail > 0 ? Math.max(0.55, avail / need) : 1;
      setScale((prev) => (Math.abs(prev - next) > 0.004 ? next : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(o);
    ro.observe(i);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={outer}
      className="flex h-full w-full flex-col overflow-hidden"
      style={{ justifyContent: scale < 1 ? 'flex-start' : 'center' }}
    >
      <div
        ref={inner}
        className={`w-full shrink-0 ${className}`}
        style={scale < 1 ? { transform: `scale(${scale})`, transformOrigin: 'top center' } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

/** Bottom sheet dialog. The sheet may scroll internally – the screens never do. */
export function Sheet({
  open,
  onClose,
  title,
  anim,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  anim: boolean;
  children: React.ReactNode;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const returnFocus = useRef<HTMLElement | null>(null);
  const closeLabel = useT().buttons.close;

  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => closeRef.current?.focus(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        // Simple focus trap.
        const els = dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        );
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey, true);
      returnFocus.current?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);

  const d = anim ? 0.22 : 0;
  return (
    <AnimatePresence>
      {open && (
        <m.div
          key="sheet"
          data-motion={anim ? 'on' : 'off'}
          className="fk-sheet fixed inset-0 z-[70] flex items-end justify-center text-pstax-navy sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: d }}
        >
          <button
            type="button"
            aria-label={closeLabel}
            tabIndex={-1}
            className="absolute inset-0 bg-pstax-navy/40"
            onClick={onClose}
          />
          <m.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative max-h-[82dvh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-t-2xl border-t border-pstax-border bg-white px-5 pt-4 shadow-xl sm:rounded-2xl sm:border sm:px-7"
            style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
            initial={anim ? { y: 24, opacity: 0 } : false}
            animate={{ y: 0, opacity: 1 }}
            exit={anim ? { y: 24, opacity: 0 } : { opacity: 0 }}
            transition={{ duration: d, ease: EASE }}
          >
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-pstax-border sm:hidden" aria-hidden="true" />
            <div className="mb-2 flex items-start justify-between gap-4">
              <h2 id={titleId} className="pt-1.5 text-xl font-bold leading-snug">
                {title}
              </h2>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-pstax-border hover:bg-pstax-bg focus-visible:outline-2 focus-visible:outline-pstax-link"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            {children}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

/** Paragraphs of a `detail` ({ title, body[] }), for use inside a sheet. */
export function DetailBody({ detail, lead }: { detail?: Detail; lead?: string }) {
  return (
    <div className="space-y-3 border-l-2 border-pstax-blue pl-4 text-[15px] leading-relaxed text-pstax-navy/85">
      {lead && <p className="font-medium text-pstax-navy">{lead}</p>}
      {detail?.body.map((p) => <p key={p}>{p}</p>)}
    </div>
  );
}

/** Small text button that opens a sheet ("Mehr dazu"). */
export function InfoButton({
  label,
  expanded,
  onClick,
  className = '',
  compact = false,
}: {
  label: string;
  expanded: boolean;
  onClick: () => void;
  className?: string;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={expanded}
      onClick={onClick}
      className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-md text-left text-sm font-semibold text-pstax-link hover:underline focus-visible:outline-2 focus-visible:outline-pstax-link ${
        compact ? '-my-2' : ''
      } ${className}`}
    >
      <Info size={16} className="shrink-0" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

/** Debounced copy of a value – keeps aria-live from announcing every slider tick. */
export function useDebounced<T>(value: T, delay = 600) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Tweened number (instant when animations are off). */
export function useCountUp(target: number, anim: boolean, duration = 350) {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    if (!anim) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const v = from + (target - from) * (1 - Math.pow(1 - t, 3));
      fromRef.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, anim, duration]);
  return Math.round(value / 100) * 100;
}

/* ---------------------------------------------------------- typography */

/** Screen heading – receives focus on every screen change. */
export function Heading({
  children,
  xl = false,
  compact = false,
}: {
  children: React.ReactNode;
  xl?: boolean;
  compact?: boolean;
}) {
  return (
    <h1
      data-screen-heading
      tabIndex={-1}
      className={`font-display font-bold text-pstax-navy outline-none ${xl ? 'fk-h1-xl' : compact ? 'fk-h2' : 'fk-h1'}`}
    >
      {children}
    </h1>
  );
}

/** Small kicker above a heading (V2.1 style: link-blue, uppercase). */
export function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-pstax-link">{children}</p>
  );
}

/** V2.1 badges: "Du" = your step (amber), "PSTax" = we do it (blue). */
export function WhoBadge({ you }: { you?: boolean }) {
  const t = useT();
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[11px] font-bold leading-none text-pstax-navy ${
        you ? 'border-[#FFBF00] bg-[#FFBF00]/15' : 'border-pstax-blue bg-pstax-blue/15'
      }`}
    >
      {you ? t.who.you : t.who.us}
    </span>
  );
}
